import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { DataManager, Visualisation, Toolbar, Chart, FilterBar } from 'wc-visualisation';
import { Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv } from 'wc-lib';
import * as screenfull from 'screenfull';
import { interpolateHtmlColor } from '../../../helpers/color';
import { getAlphanumericString } from '../../../helpers/stringHelper';
import { insertMissingIntervals } from './dataUtils';
// import { FPP_RCR_testData } from './test-data/FPP_RCR-test-data';
// import { FPP_USAGE_testData } from './test-data/FPP_USAGE-test-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./fpp-rcr-and-usage.html'),
  name: 'fpp-rcr-and-usage',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class FppRcrAndUsage extends Vue {
  constraintIds = [];
  selectedConstraintId: string = '';

  loadMessage: string = '';
  dataCache: Record<string, any[]> = {};
  chartData: any[] = [];
  chartConfig: any = null;
  tableData: any[] = [];

  intervalRefresh: any;

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    this.loadData(this.selectedConstraintId, true);

    this.intervalRefresh = setInterval(() => {
      this.loadData(this.selectedConstraintId, true);
    }, 300 * 1000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }

  @Watch('selectedConstraintId')
  onSelectedConstraintIdChange(newValue: string, oldValue: string): void {
    this.selectedConstraintId = newValue;
    this.loadData(this.selectedConstraintId, false);
  }

  loadData(constraintId: string, clearDataCache: boolean) {
    this.loadMessage = 'Loading data';
    this.fetchDataAsync(clearDataCache).then(
      (data) => {
        this.constraintIds = Array.from(new Set<string>(data.rcr.map(x => x.CONSTRAINTID))).sort();
        if (this.constraintIds.indexOf(constraintId) === -1) {
          this.selectedConstraintId = this.constraintIds[0];
          constraintId = this.constraintIds[0];
        }

        this.tableData = this.combineRcrAndUsageData(data.rcr, data.usage, this.selectedConstraintId);
        this.chartData = insertMissingIntervals(this.tableData, 'INTERVAL_DATETIME', 300, { CONSTRAINTID: constraintId, RCR: 0, USAGE: 0 });
        this.chartConfig = this.getChartConfig();

        this.loadMessage = '';
      },
      (error) => {
        this.loadMessage = 'No data found';
      });
  }

  async fetchDataAsync(clearDataCache: boolean): Promise<{ rcr: any[], usage: any[] }> {
    if (clearDataCache) {
      this.dataCache = {};
    }

    let rcrReportName = 'FPP_RCR';
    if (!this.dataCache[rcrReportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + rcrReportName, undefined, 60000, rcrReportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['INTERVAL_DATETIME']);
      let data = DataManager.getDynamicView(id, rcrReportName).data();

      // let data = FPP_RCR_testData.map((x) => ({ ...x, INTERVAL_DATETIME: new Date(new Date(Date.parse(x.INTERVAL_DATETIME))) }));

      this.dataCache[rcrReportName] = data;
    }

    let usageReportName = 'FPP_USAGE';
    if (!this.dataCache[usageReportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + usageReportName, undefined, 60000, usageReportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['INTERVAL_DATETIME']);
      let data = DataManager.getDynamicView(id, usageReportName).data();

      // let data = FPP_USAGE_testData.map((x) => ({ ...x, INTERVAL_DATETIME: new Date(new Date(Date.parse(x.INTERVAL_DATETIME))) }))

      this.dataCache[usageReportName] = data;
    }

    return { rcr: this.dataCache[rcrReportName], usage: this.dataCache[usageReportName] }
  }

  combineRcrAndUsageData(rcrData: any[], usageData: any[], constraintId: string): any[] {
    let constraintUsageData = usageData.filter(x => x.CONSTRAINTID === constraintId);
    return rcrData
      .filter(x => x.CONSTRAINTID === constraintId)
      .map((x) => ({ ...x, USAGE: this.getMatchingUsage(x, constraintUsageData) }));
  }

  getMatchingUsage(rcr: any, usageData: any[]): number {
    let matchingUsageData = usageData.find(x => x.INTERVAL_DATETIME.getTime() === rcr.INTERVAL_DATETIME.getTime() && x.CONSTRAINTID === rcr.CONSTRAINTID);
    return matchingUsageData ? matchingUsageData.USAGE : undefined;
  }

  getChartConfig() {
    let rcrColor = '#34B9B3';
    let usageColor = '#B056BC';

    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 10,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 5,
        decimalSeparator: '.',
        thousandsSeparator: ','
      },
      valueAxes: [
        {
          id: "rcr",
          position: "left",
          labelsEnabled: true,
          gridAlpha: 0,
          axisAlpha: 0,
          color: '#8ea9a9',
          title: 'RCR',
          titleColor: rcrColor
        },
        {
          id: "usage",
          position: "right",
          labelsEnabled: true,
          gridAlpha: 0,
          axisAlpha: 0,
          color: '#8ea9a9',
          title: 'USAGE',
          titleColor: usageColor
        }
      ],
      graphs: [
        {
          valueAxis: "rcr",
          balloonText: 'RCR [[value]]',
          lineColor: rcrColor,
          valueField: 'RCR',
          lineThickness: 3,
          fillAlphas: 0.3,
          fillColors: [rcrColor, interpolateHtmlColor(rcrColor, '#ffffff', 0.3)],
          balloonColor: '#000000',
          balloon: {
            color: '#ffffff',
            fillColor: '#000000',
            showBullet: false
          }
        },
        {
          valueAxis: "usage",
          balloonText: 'USAGE [[value]]',
          lineColor: usageColor,
          valueField: 'USAGE',
          lineThickness: 3,
          balloonColor: '#000000',
          balloon: {
            color: '#ffffff',
            fillColor: '#000000',
            showBullet: false
          }
        }
      ],
      categoryAxis: {
        parseDates: true,
        minPeriod: "mm",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "INTERVAL_DATETIME",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function (value) {
          return formatDate(value, "HH:mm dd MMM");
        }
      }
    };
  }

  //
  // download
  //

  tableColumns = [
    {
      title: 'Constraint ID',
      field: 'CONSTRAINTID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Interval Date Time',
      field: 'INTERVAL_DATETIME',
      formatter: (value) => {
        let date = value.INTERVAL_DATETIME || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'RCR',
      field: 'RCR',
      fieldType: FieldType.Number,
      sortable: true
    },
    {
      title: 'Usage',
      field: 'USAGE',
      fieldType: FieldType.Number,
      sortable: true
    },
  ];

  download() {
    let rows = this.tableData;
    let columns = this.tableColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(`FPP_RCR_USAGE_${getAlphanumericString(rows[0].CONSTRAINTID)}_${formatDate(rows[0].INTERVAL_DATETIME, 'yyyyMMddHHmm')}.csv`, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
    setTimeout(() => {
      if (!this.isFullscreen) {
        this.loadData(this.selectedConstraintId, false);
      }
    }, 2000); // delay gives screen.isFullscreen time to update after screen.toggle()
  }
}



// WEBPACK FOOTER //
// src/views/electricity/fpp/fpp-rcr-and-usage.ts